import { ProductTypeEnum } from "@kanpla/api-contract";

import { round } from "@repo/system";
import type {
  Discount,
  Product,
  StampCardDiscount,
  UnitSystem
} from "~/api/products/types";
import { type StockStatus } from "~/utils/stock/infer-stock-status";
import { calculateUnitPriceByUnitSystem } from "~/utils/calculateUnitPriceByUnitSystem";
import { roundAwayFromZero } from "~/utils/round-away-from-zero";
import {
  type SaleEngineLine,
  type SaleEngineLineDiscount
} from "~/lib/sale-engine";
import { resolveStampCardBaseDiscount } from "~/lib/sale-engine";

import { type BasketItem } from "../../types";

import { calculateVariantsGrossPrice } from "./calculate-variants-gross-price";
import { mapBasketChoicesToVariants } from "./map-basket-choices-to-variants";
import {
  resolveLineDeposit,
  resolveVariantDeposits
} from "./resolve-line-deposit";
import {
  type KioskDisplayBasketItem,
  type KioskDisplayProductBasket,
  type KioskDisplayProductBasketVariantChoice
} from "./types";

type PrepareKioskBasketLineArgs = {
  product: Product;
  productInBasket: BasketItem;
  discounts: Discount[];
  stampCardDiscounts: StampCardDiscount[] | undefined;
  isLimitOver: boolean;
  stockStatus: StockStatus;
};

// Captured at prepare, consumed by `projectKioskLine`.
export type KioskLineMeta = {
  basketItemId: string;
  quantity: number;
  // Per-unit catalog excl-VAT (base only) — the ordering payload's `unitPrice`.
  unitPriceExclVat: number;
  // Line excl base incl. variants/weight (mirrors POS), scaled by the projector.
  unitTotalPriceExclVatBase: number;
  depositTotalExclVat: number;
  productDepositTotalInclVat: number;
  productDepositTotalExclVat: number;
  variantDepositTotals: KioskDisplayBasketItem["variantDepositTotals"];
  vatRate: number;
  isRedeemed: boolean;
  // "numberOfProducts" set here; "unitPrice" filled by the projector.
  appliedAllowanceType: "unitPrice" | "numberOfProducts" | null;
  displayProduct: KioskDisplayProductBasket;
};

export type PreparedKioskLine = {
  engineLine: SaleEngineLine;
  meta: KioskLineMeta;
  product: Product;
  productInBasket: BasketItem;
};

const toLineDiscount = (
  amountType: string,
  amount: number
): SaleEngineLineDiscount =>
  amountType === "percentage"
    ? { kind: "percentage", amount }
    : { kind: "currency", amount };

/** Build the sale-engine line + projection meta for one kiosk basket item. */
export function prepareKioskBasketLine({
  product,
  productInBasket,
  discounts,
  stampCardDiscounts,
  isLimitOver,
  stockStatus
}: PrepareKioskBasketLineArgs): PreparedKioskLine {
  let baseUnitPriceInclVat = product.unitPriceInclVat;
  let unitPriceExclVatPerUnit = product.unitPriceExclVat;
  let variantsPriceInclVat = 0;
  let variantsPriceExclVat = 0;
  let isRedeemed = false;
  let appliedAllowanceType: KioskLineMeta["appliedAllowanceType"] = null;

  const choices: KioskDisplayProductBasketVariantChoice[] = [];

  if (
    productInBasket.type === "variant" &&
    product.type === ProductTypeEnum.VARIANTS
  ) {
    const variantChoiceMap = mapBasketChoicesToVariants(
      productInBasket.choices,
      product.options.flatMap((option) =>
        option.choices.map((choice) => ({
          ...choice,
          optionId: option.id
        }))
      )
    );
    variantsPriceInclVat = calculateVariantsGrossPrice(variantChoiceMap);

    for (const { choice, quantity } of variantChoiceMap.values()) {
      variantsPriceExclVat += round(choice.unitPriceExclVat ?? 0, 2) * quantity;

      if (!choice.name) continue;

      choices.push({
        id: choice.id,
        name: choice.name,
        allergens: choice.allergens ?? [],
        quantity
      });
    }
  }

  const isWeightedProduct =
    productInBasket.type === "weighted" &&
    "unitSystem" in product &&
    product.unitSystem !== "piece";

  if (isWeightedProduct && productInBasket.type === "weighted") {
    baseUnitPriceInclVat = calculateUnitPriceByUnitSystem({
      unitPrice: product.unitPriceInclVat,
      weight: productInBasket.weight,
      unitSystem: product.unitSystem,
      weightUnit: productInBasket.weightUnit
    });
    unitPriceExclVatPerUnit = calculateUnitPriceByUnitSystem({
      unitPrice: product.unitPriceExclVat,
      weight: productInBasket.weight,
      unitSystem: product.unitSystem,
      weightUnit: productInBasket.weightUnit
    });
  }

  // Per-line discounts; unitPrice allowances are skipped — the engine's allowance
  // stage applies them (capped). Classification mirrors POS: read it off the
  // discount def (`allowanceType`), not a runtime allowance set.
  const lineDiscounts: SaleEngineLineDiscount[] = [];
  for (const productInBasketDiscount of productInBasket.discounts) {
    const isLineAllowanceDiscount =
      product.discount?.id === productInBasketDiscount.id;

    if (
      isLineAllowanceDiscount &&
      product.discount?.allowanceType === "unitPrice"
    ) {
      continue;
    }

    const discount = discounts.find((d) => d.id === productInBasketDiscount.id);
    if (discount) {
      lineDiscounts.push(toLineDiscount(discount.amountType, discount.amount));

      if (
        isLineAllowanceDiscount &&
        product.discount?.allowanceType === "numberOfProducts"
      ) {
        appliedAllowanceType = "numberOfProducts";
      }
    }

    const stampCardDiscount = stampCardDiscounts?.find(
      (d) => d.stampCardRuleId === productInBasketDiscount.id
    );
    if (stampCardDiscount) {
      isRedeemed = true;
      lineDiscounts.push({
        kind: "flat",
        amount: resolveStampCardBaseDiscount({
          baseUnitPriceInclVat,
          discountType: stampCardDiscount.discountType,
          discount: stampCardDiscount.discount
        })
      });
    }
  }

  // Gates the engine's unitPrice-allowance stage.
  const hybridBillingDiscountId = productInBasket.discounts.find(
    (d) => d.type === "hybrid-billing"
  )?.id;

  const lineDeposit = resolveLineDeposit(product);
  const variantDeposits = resolveVariantDeposits(
    product,
    productInBasket.type === "variant" ? productInBasket.choices : []
  );

  const unitDeposit = (
    productInBasket.type === "variant" ? productInBasket.choices : []
  ).reduce(
    (acc, choice) => {
      const deposit = variantDeposits[choice.id];
      if (!deposit) return acc;

      return {
        inclVat: acc.inclVat + deposit.feeInclVat * choice.quantity,
        exclVat: acc.exclVat + deposit.feeExclVat * choice.quantity
      };
    },
    {
      inclVat: lineDeposit?.feeInclVat ?? 0,
      exclVat: lineDeposit?.feeExclVat ?? 0
    }
  );

  const variantDepositTotals = Object.fromEntries(
    (productInBasket.type === "variant" ? productInBasket.choices : []).flatMap(
      (choice) => {
        const deposit = variantDeposits[choice.id];
        if (!deposit) return [];

        const units = choice.quantity * productInBasket.quantity;
        return [
          [
            choice.id,
            {
              inclVat: deposit.feeInclVat * units,
              exclVat: deposit.feeExclVat * units
            }
          ] as const
        ];
      }
    )
  );

  const engineLine: SaleEngineLine = {
    basketId: productInBasket.id,
    quantity: productInBasket.quantity,
    // Per-unit incl-VAT (base + rounded variants); engine multiplies by qty.
    unitCompositeInclVat:
      roundAwayFromZero(baseUnitPriceInclVat) + variantsPriceInclVat,
    eligibleForOrderDiscount: baseUnitPriceInclVat > 0,
    discounts: lineDiscounts,
    allowanceDiscountIds: hybridBillingDiscountId
      ? [hybridBillingDiscountId]
      : [],
    unitDepositInclVat: unitDeposit.inclVat
  };

  // No pricing stage touches the deposit, so its excl base needs none of the
  // scaling `discountedExclVat` does for discounted product money.
  const depositTotalExclVat = unitDeposit.exclVat * productInBasket.quantity;

  const baseProduct = {
    id: product.id,
    name: product.name,
    description: product.description,
    tags: product.tags ?? [],
    allergens: product.allergensDetailed,
    imageUrl: product.imageUrl,
    categoryId: product.categoryId,
    barcodeIds: product.barcodeIds,
    stamps: product.stamps,
    choices: [],
    isLimitOver,
    stockStatus,
    disabled:
      isLimitOver ||
      stockStatus === "reserved" ||
      stockStatus === "out-of-stock",
    remaining: null,
    orderMin: product.orderMin
  };

  let displayProduct: KioskDisplayProductBasket;

  if (isWeightedProduct) {
    displayProduct = {
      ...baseProduct,
      type: "weighted",
      unitSystem: product.unitSystem as Exclude<UnitSystem, "piece">,
      containerWeight: product.containerWeight,
      weight: productInBasket.weight,
      weightUnit: productInBasket.weightUnit,
      weightDecimals: productInBasket.weightDecimals
    };
  } else if (productInBasket.type === "variant") {
    displayProduct = {
      ...baseProduct,
      type: "variant",
      choices
    };
  } else {
    displayProduct = {
      ...baseProduct,
      type: "regular"
    };
  }

  return {
    engineLine,
    meta: {
      basketItemId: productInBasket.id,
      quantity: productInBasket.quantity,
      unitPriceExclVat: product.unitPriceExclVat,
      unitTotalPriceExclVatBase:
        (unitPriceExclVatPerUnit + variantsPriceExclVat) *
        productInBasket.quantity,
      depositTotalExclVat,
      productDepositTotalInclVat:
        (lineDeposit?.feeInclVat ?? 0) * productInBasket.quantity,
      productDepositTotalExclVat:
        (lineDeposit?.feeExclVat ?? 0) * productInBasket.quantity,
      variantDepositTotals,
      vatRate: product.taxRate.vatRate,
      isRedeemed,
      appliedAllowanceType,
      displayProduct
    },
    product,
    productInBasket
  };
}
